import { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Divider,
  FileButton,
  Group,
  NumberInput,
  Select,
  Stack,
  Text,
} from '@mantine/core';
import { IconInfoCircle, IconUpload } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';
import {
  detectBodyVariables,
  extractBodyText,
  extractButtons,
  extractHeaderFormat,
  extractId,
  hasComponent,
  type RawTemplate,
} from '../../lib/template-introspection';
import { buildPreviewTemplate } from '../../lib/template-preview-builder';
import { TemplatePreview } from '../TemplatePreview';
import { CarouselSendEditor, detectCarouselCards } from '../templates/CarouselSendEditor';
import type { CarouselSendCardInput } from '../../../../api/src/modules/campaigns/schema';
import { TokenTextInput, type TokenOption } from './TokenTextInput';

/**
 * Full-parity template configurator for a bot-flow `template` node. Replaces
 * the old hardcoded mock list — pulls real approved templates and exposes the
 * same parameter surface the 1:1 chat "Send a template" modal does (body
 * variables, header media/text, URL-button suffix, coupon code, LTO
 * expiration, carousel), with static-or-token values everywhere.
 *
 * The node `data` shape it reads/writes (all additive to legacy nodes):
 *   templateName, languageCode, templateId, bodyVariables[], bodyVariableNames[],
 *   headerMedia{assetId,format,fileName,mimeType}, headerText,
 *   urlButtonText, urlButtonIndex, couponCode, couponCodeButtonIndex,
 *   ltoExpiresInHours, carousel[]
 */
export function FlowTemplatePanel({
  data,
  onChange,
  tokenOptions,
}: {
  data: Record<string, unknown>;
  onChange: (patch: Record<string, unknown>) => void;
  tokenOptions: TokenOption[];
}) {
  const templates = trpc.whatsapp.templates.list.useQuery();
  const uploadMedia = trpc.bots.flowMedia.upload.useMutation();

  const approved = useMemo(
    () => (templates.data ?? []).filter((t) => (t as { status?: string }).status === 'APPROVED'),
    [templates.data],
  );

  const selected = useMemo(() => {
    const name = data.templateName as string | undefined;
    const lang = data.languageCode as string | undefined;
    if (!name) return null;
    return (
      approved.find(
        (t) =>
          (t as { name?: string }).name === name &&
          (!lang || (t as { language?: string }).language === lang),
      ) ?? null
    );
  }, [approved, data.templateName, data.languageCode]);

  const detected = useMemo(() => detectBodyVariables(extractBodyText(selected)), [selected]);
  const headerFormat = (extractHeaderFormat(selected) ?? 'NONE').toUpperCase();
  const buttons = useMemo(() => extractButtons(selected), [selected]);
  const urlButtonIndex = buttons.findIndex(
    (b) => b.type === 'URL' && (b.url ?? '').includes('{{1}}'),
  );
  const copyCodeIndex = buttons.findIndex((b) => b.type === 'COPY_CODE');
  const hasLto = hasComponent(selected, 'LIMITED_TIME_OFFER');
  const carouselCards = useMemo(() => detectCarouselCards(selected), [selected]);

  const bodyVariables = useMemo(
    () => (data.bodyVariables as string[] | undefined) ?? [],
    [data.bodyVariables],
  );
  const headerMedia = data.headerMedia as
    | { assetId?: string; format?: string; fileName?: string }
    | undefined;

  // Local-only (never persisted to node data) — a blob URL for the "live
  // preview" below, same pattern templates.new.tsx uses for its own preview
  // pane. Reset whenever the template selection changes.
  const [headerPreviewUrl, setHeaderPreviewUrl] = useState<string | null>(null);

  const previewTemplate = useMemo(() => {
    if (!selected || carouselCards) return null;
    return buildPreviewTemplate(selected as { components?: unknown[]; category?: string | null }, {
      bodyVariables,
      headerMediaUrl: headerPreviewUrl,
      headerText: (data.headerText as string | undefined) ?? '',
      limitedTimeOfferExpiresAtMs:
        typeof data.ltoExpiresInHours === 'number' && data.ltoExpiresInHours > 0 ? 1 : undefined,
    });
  }, [
    selected,
    carouselCards,
    bodyVariables,
    headerPreviewUrl,
    data.headerText,
    data.ltoExpiresInHours,
  ]);

  const onSelectTemplate = (value: string | null) => {
    setHeaderPreviewUrl(null);
    if (!value) {
      onChange({
        templateName: undefined,
        languageCode: undefined,
        templateId: undefined,
        bodyVariables: undefined,
        bodyVariableNames: undefined,
        headerMedia: undefined,
        headerText: undefined,
        urlButtonText: undefined,
        urlButtonIndex: undefined,
        couponCode: undefined,
        couponCodeButtonIndex: undefined,
        ltoExpiresInHours: undefined,
        carousel: undefined,
      });
      return;
    }
    const t = approved.find((x) => String(extractId(x as RawTemplate)) === value) as
      | RawTemplate
      | undefined;
    if (!t) return;
    const det = detectBodyVariables(extractBodyText(t));
    onChange({
      templateName: t.name,
      languageCode: t.language,
      templateId: extractId(t),
      bodyVariables: det.names.map(() => ''),
      bodyVariableNames: det.format === 'named' ? det.names : undefined,
      // Reset parameter bindings that don't carry over between templates.
      headerMedia: undefined,
      headerText: undefined,
      urlButtonText: undefined,
      urlButtonIndex: urlButtonIndexFor(t),
      couponCode: undefined,
      couponCodeButtonIndex: undefined,
      ltoExpiresInHours: undefined,
      carousel: undefined,
    });
  };

  const setBodyVar = (i: number, val: string) => {
    const next = [...bodyVariables];
    next[i] = val;
    onChange({ bodyVariables: next });
  };

  const uploadHeader = (file: File | null) => {
    if (!file) return;
    setHeaderPreviewUrl(URL.createObjectURL(file));
    void file.arrayBuffer().then((buf) => {
      const bytes = new Uint8Array(buf);
      let bin = '';
      for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
      uploadMedia.mutate(
        { base64: btoa(bin), mimeType: file.type, filename: file.name },
        {
          onSuccess: (r) => {
            onChange({
              headerMedia: {
                assetId: r.assetId,
                format: headerFormat,
                fileName: r.fileName,
                mimeType: r.mimeType,
              },
            });
            notifications.show({ message: 'Header media uploaded', color: 'green' });
          },
          onError: (err) => notifications.show({ message: err.message, color: 'red' }),
        },
      );
    });
  };

  return (
    <Stack gap="md">
      {templates.isPending ? (
        <Text size="sm" c="dimmed">
          Loading templates…
        </Text>
      ) : (
        <Select
          label="Meta template"
          description="Only approved templates can be sent"
          placeholder="Choose template…"
          searchable
          data={approved.map((t) => ({
            value: String(extractId(t as RawTemplate)),
            label: `${(t as { name?: string }).name} (${(t as { language?: string }).language})`,
          }))}
          value={data.templateId != null ? String(data.templateId) : null}
          onChange={onSelectTemplate}
        />
      )}

      {!data.templateName && (
        <Alert color="gray" variant="light" icon={<IconInfoCircle size={16} />}>
          Select a template to configure its parameters.
        </Alert>
      )}

      {previewTemplate && (
        <Stack gap={4}>
          <Text size="sm" fw={500}>
            Live preview
          </Text>
          <TemplatePreview template={previewTemplate} />
        </Stack>
      )}

      {selected && carouselCards && (
        <CarouselSendEditor
          cards={carouselCards}
          onChange={(c: CarouselSendCardInput[]) => onChange({ carousel: c })}
        />
      )}

      {selected && !carouselCards && (
        <>
          {(headerFormat === 'IMAGE' ||
            headerFormat === 'VIDEO' ||
            headerFormat === 'DOCUMENT') && (
            <Stack gap={4}>
              <Text size="sm" fw={500}>
                Header {headerFormat.toLowerCase()}
              </Text>
              <Group>
                <FileButton
                  onChange={uploadHeader}
                  accept={
                    headerFormat === 'IMAGE'
                      ? 'image/*'
                      : headerFormat === 'VIDEO'
                        ? 'video/*'
                        : 'application/pdf'
                  }
                >
                  {(props) => (
                    <Button
                      {...props}
                      size="xs"
                      variant="light"
                      leftSection={<IconUpload size={12} />}
                      loading={uploadMedia.isPending}
                    >
                      Upload {headerFormat.toLowerCase()}
                    </Button>
                  )}
                </FileButton>
                {headerMedia?.assetId && (
                  <Badge color="green" variant="light">
                    {headerMedia.fileName ?? 'Uploaded'} ✓
                  </Badge>
                )}
              </Group>
              <Text size="xs" c="dimmed">
                Same media shown to every contact this flow reaches.
              </Text>
            </Stack>
          )}

          {headerFormat === 'TEXT' && (
            <TokenTextInput
              label="Header text"
              placeholder="Header line (supports variables)"
              value={(data.headerText as string | undefined) ?? ''}
              onChange={(v) => onChange({ headerText: v })}
              options={tokenOptions}
            />
          )}

          {detected.names.length > 0 && (
            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm" fw={500}>
                  Body variables
                </Text>
                <Badge size="xs" variant="light" color="orange">
                  {detected.names.length} required
                </Badge>
              </Group>
              <Text size="xs" c="dimmed">
                Static text or a variable (insert with the {'{ }'} button).
              </Text>
              {detected.names.map((name, i) => (
                <TokenTextInput
                  key={name}
                  label={detected.format === 'named' ? `{{${name}}}` : `{{${name}}}`}
                  size="xs"
                  placeholder="e.g. static text or a variable"
                  value={bodyVariables[i] ?? ''}
                  onChange={(v) => setBodyVar(i, v)}
                  options={tokenOptions}
                />
              ))}
            </Stack>
          )}

          {urlButtonIndex >= 0 && (
            <TokenTextInput
              label="Link for the URL button"
              description="Fills the button's {{1}} — e.g. a tracking or booking link."
              value={(data.urlButtonText as string | undefined) ?? ''}
              onChange={(v) => onChange({ urlButtonText: v, urlButtonIndex: urlButtonIndex })}
              options={tokenOptions}
            />
          )}

          {copyCodeIndex >= 0 && (
            <TokenTextInput
              label="Coupon / offer code"
              value={(data.couponCode as string | undefined) ?? ''}
              onChange={(v) => onChange({ couponCode: v, couponCodeButtonIndex: copyCodeIndex })}
              options={tokenOptions}
            />
          )}

          {hasLto && (
            <>
              <Divider />
              <NumberInput
                label="Offer expires in (hours)"
                description="Countdown starts when each contact receives the message — evergreen-flow friendly. Leave blank for no countdown."
                min={1}
                max={720}
                value={(data.ltoExpiresInHours as number | undefined) ?? ''}
                onChange={(v) =>
                  onChange({ ltoExpiresInHours: typeof v === 'number' ? v : undefined })
                }
              />
            </>
          )}
        </>
      )}
    </Stack>
  );
}

function urlButtonIndexFor(t: RawTemplate): number | undefined {
  const buttons = extractButtons(t);
  const idx = buttons.findIndex((b) => b.type === 'URL' && (b.url ?? '').includes('{{1}}'));
  return idx >= 0 ? idx : undefined;
}
